import { useEffect } from "react";

/**
 * Injects a BreadcrumbList JSON-LD <script> into <head> for the
 * current route, then removes it on unmount. Trail is an ordered
 * array of { name, url } items starting from Home.
 *
 * The script is identified by data-breadcrumb so prerender / dev
 * tools can find it, and so we can clean up exactly the right
 * element on unmount (instead of nuking any unrelated JSON-LD).
 *
 * Captured by the prerender pass via the 700ms post-load wait —
 * which means the static HTML emitted for each route ships with
 * its own BreadcrumbList in <head>, visible to crawlers.
 */
export default function useBreadcrumbJsonLd(trail) {
    useEffect(() => {
        if (!trail || trail.length === 0) return;

        const data = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: trail.map((step, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: step.name,
                item: step.url,
            })),
        };

        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-breadcrumb", "true");
        el.textContent = JSON.stringify(data);
        // Appended to #root (not <head>) so the prerender pass, which
        // snapshots #root, captures the breadcrumb in the static HTML.
        // Google parses JSON-LD from anywhere in the document — no SEO
        // impact, just a delivery detail.
        const host = document.getElementById("root") || document.head;
        host.appendChild(el);

        return () => {
            if (el.parentNode) el.parentNode.removeChild(el);
        };
    }, [trail]);
}
